"use client"

import styled from "@emotion/styled"
import { BranchCard } from "@eurodrive/ui"
import Container from "@eurodrive/ui/Container"
import ListBranches from "@eurodrive/ui/icons/ListBranches"
import MapBranches from "@eurodrive/ui/icons/MapBranches"
import { findBranchImage } from "@eurodrive/utils"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import { branchLocatorClicked } from "@eurodrive/utils/gtm"
import { Tab, Tabs } from "@mui/material"
import { APIProvider, Map } from "@vis.gl/react-google-maps"
import { BranchDto } from "api"
import { useSearchParams } from "next/navigation"
import { useEffect, useRef, useState } from "react"
import CustomMarker from "./CustomMarket"

const API_KEY = process.env.GOOGLE_API_KEY

const MobileLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-top: 40px;
  padding-bottom: 64px;
`

const ListColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 35px;
`

const MapContainer = styled.div`
  width: 100%;
  height: 506px;
`

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  )
}

export default function MobileMapComponent({
  branches,
  brancheImages,
}: {
  branches: BranchDto[]
  brancheImages: {
    branchNumber: string
    thumbnail: string
    thumbnailMobile: string
  }[]
}) {
  const searchParams = useSearchParams()
  const [selectedBranch, setSelectedBranch] = useState<BranchDto | null>(null)
  const [value, setValue] = useState(0)
  const branchRefs = useRef<{ [key: number]: HTMLDivElement | null }>({})
  const containerRef = useRef<HTMLDivElement | null>(null)
  // undefined when the CMS filtered every branch out; the map is skipped rather
  // than handed an invalid centre.
  const mapCenterBranch = branches[0]

  // Pre-select branch from URL query parameter
  useEffect(() => {
    const branchIdParam = searchParams.get("branchId")
    if (branchIdParam) {
      const branch = branches.find((b) => b.id === Number(branchIdParam))
      if (branch) {
        setSelectedBranch(branch)
        // Stay on list tab (value = 0)
        setValue(0)
        // Scroll to the selected branch in the list
        setTimeout(() => {
          const branchElement = branchRefs.current[branch.id]
          if (branchElement) {
            const elementPosition = branchElement.getBoundingClientRect().top
            const offsetPosition = elementPosition + window.pageYOffset - 100

            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth",
            })
          }
        }, 300)
      }
    }
  }, [searchParams, branches])

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }
  const handleBranchSelect = (branch: BranchDto) => {
    branchLocatorClicked(branch.branchName, "branches_page")
    setSelectedBranch(selectedBranch?.id === branch.id ? null : branch)
  }
  return (
    <Container paddingMobile>
      <MobileLayout>
        <Tabs value={value} onChange={handleChange}>
          <Tab icon={<MapBranches />} label="רשימה" />
          <Tab icon={<ListBranches />} label="מפה" />
        </Tabs>
        <CustomTabPanel value={value} index={0}>
          <ListColumn>
            {branches.map((branch, i) => (
              <div
                key={i}
                ref={(el) => {
                  branchRefs.current[branch.id] = el
                }}
                style={{
                  background:
                    selectedBranch?.id === branch.id
                      ? "#f5f5f5"
                      : "transparent",
                  borderRadius: "4px",
                  transition: "background 0.3s ease",
                }}
              >
                <BranchCard
                  branch={branch}
                  desktopImage={getUmbracoMediaUrl(
                    findBranchImage(brancheImages, branch)?.thumbnailMobile ||
                      "",
                  )}
                />
              </div>
            ))}
          </ListColumn>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <MapContainer>
            <APIProvider apiKey={API_KEY!}>
              {mapCenterBranch && (
                <Map
                  mapTypeControl={false}
                  defaultCenter={{
                    lat: mapCenterBranch.latitude,
                    lng: mapCenterBranch.longitude,
                  }}
                  defaultZoom={10}
                  mapId="c4cbf2b3fb26b849b070fc87"
                >
                  {branches.map((branch) => (
                    <CustomMarker
                      key={branch.id}
                      branch={branch}
                      isSelected={selectedBranch?.id === branch.id}
                      onClick={() => handleBranchSelect(branch)}
                      branchImage={
                        findBranchImage(brancheImages, branch)?.thumbnailMobile
                      }
                    />
                  ))}
                </Map>
              )}
            </APIProvider>
          </MapContainer>
        </CustomTabPanel>
      </MobileLayout>
    </Container>
  )
}
